/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from 'ducks/main-dashboard/selectors';
import useAppLocalStorage from '_clean-code/utils/localStorageHelper';
import { MachineGroupSelectorType, MachineOption, MachineGroup } from 'types';
import { I18n, Translate } from '../../../utils/i18n';
import styles from './index.module.scss';
import { selectMachineGroups } from '../../../ducks/user/actions';
import GlobalFilterButton from './components/GlobalFilterButton';
import MachineGroupsContainer from './components/MachineGroupsContainer';
import MachinesContainer from './components/MachinesContainer';
import ActionButtonRow from './components/ActionButtonRow';
import useOutsideClick from './utils/useOutsideClick';
import { sendEventTrack } from '@utils/analytics';
import _ from 'lodash';
interface MachineGroupSelectorProps {
  classNames?: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function MachineGroupSelector(props: MachineGroupSelectorProps) {
  const currentUser = useSelector(userSelector);
  const appLocalStorage = useAppLocalStorage(currentUser.companyId);
  const machineGroups: MachineGroupSelectorType = useSelector(
    (store: any) => store.user.machineGroups
  );
  const machinesStore = useSelector((store: any) => store.machines);
  const allowAllMachines: boolean = useSelector(
    (store: any) => store.user.machineGroups.allowAllMachines
  );

  const [selectedMachineGroups, setSelectedMachineGroups] = useState(
    machineGroups.selectedMachineGroups
  );

  const [selectedMachineGroupId, setSelectedMachineGroupId] = useState(
    machineGroups.selectedMachineGroups[0]?.id
  );

  const [selectedMachines, setSelectedMachines] = useState(
    machineGroups.selectedMachines
  );

  const allMachineOptions = useMemo(
    () =>
      selectedMachineGroupId !== 'all-machines'
        ? _.uniqBy(
            machineGroups.list
              .filter((curr) =>
                selectedMachineGroups.map((e) => e.id).includes(curr.id)
              )
              .map((e) =>
                e.machines.map((machine) => ({
                  ...machine,
                  order:
                    machinesStore.machines.find((e) => e.id === machine.id)
                      ?.order ?? 0,
                  label: machine.title,
                  value: machine.id,
                  machineGroupId: e.id
                }))
              )
              .flat(1),
            'value'
          )
        : [],
    [
      machineGroups.list,
      selectedMachineGroupId,
      selectedMachineGroups,
      machinesStore.machines
    ]
  );

  useEffect(() => {
    setSelectedMachineGroups(machineGroups.selectedMachineGroups);
  }, [machineGroups.list, machineGroups.selectedMachineGroups]);

  const dispatch = useDispatch();

  const selectionText =
    selectMachineGroups.length == 0
      ? I18n.t('no_machine_groups')
      : `${machineGroups.selectedMachineGroups.length} ${
          machineGroups.selectedMachineGroups.length > 1
            ? I18n.t('machine_settings')
            : I18n.t('machine_group')
        }`;

  const ref = useRef(null);
  useOutsideClick(ref, () => {
    onCancel();
  });

  const onGroupSelection = (machineGroup: MachineGroup) => {
    setSelectedMachineGroupId(machineGroup.id);
    let newMachineGroups = [...selectedMachineGroups];
    const isSelected = selectedMachineGroups
      .map((e) => e.id)
      .includes(machineGroup.id);
    if (selectedMachineGroups.map((e) => e.id).includes('all-machines')) {
      const newMachines = machineGroup.machines.map((machine) => ({
        label: machine.title,
        value: machine.id,
        order:
          machinesStore.machines.find((e) => e.id === machine.id)?.order ?? 0
      }));
      newMachineGroups = [{ id: machineGroup.id, machines: newMachines }];
      setSelectedMachines(
        newMachines.map((e) => ({ ...e, machineGroupId: machineGroup.id }))
      );
    } else if (isSelected) {
      newMachineGroups = newMachineGroups.filter(
        (e) => e.id !== machineGroup.id
      );
      setSelectedMachines(
        selectedMachines.filter((e) => e.machineGroupId !== machineGroup.id)
      );
    } else if (!isSelected) {
      const machineOptions = machineGroup.machines.map((machine) => ({
        label: machine.title,
        value: machine.id,
        machineGroupId: machineGroup.id,
        order:
          machinesStore.machines.find((e) => e.id === machine.id)?.order ?? 0
      }));
      const newMachines = [...machineOptions, ...selectedMachines];
      newMachineGroups.push({ id: machineGroup.id, machines: newMachines });
      setSelectedMachines(newMachines);
    }
    setSelectedMachineGroups(newMachineGroups);
  };

  const onAllMachinesGroupSelection = () => {
    setSelectedMachineGroupId('all-machines');
    if (selectedMachineGroups.map((e) => e.id).includes('all-machines')) {
      setSelectedMachineGroups([]);
      setSelectedMachines([]);
    } else {
      const machines = machinesStore.machines.map((machine) => ({
        label: machine.title,
        value: machine.id,
        order: machine.order,
        machineGroupId: 'all-machines'
      }));
      setSelectedMachineGroups([{ id: 'all-machines', machines }]);
      setSelectedMachines(machines);
    }
  };

  const noOfUniqueSelectedMachines = _.uniqBy(selectedMachines, 'value').length;

  const onMachinesSelection = (
    option: MachineOption & { machineGroupId: string },
    removed: boolean
  ) => {
    if (removed) {
      setSelectedMachines(
        selectedMachines.filter((e) => e.value !== option.value)
      );
    } else {
      const newSelectedMachines = [
        ...selectedMachines,
        {
          label: option.label,
          value: option.value,
          machineGroupId: option.machineGroupId,
          order: option.order
        }
      ];
      setSelectedMachines(newSelectedMachines);
    }
  };

  const onAllMachinesSelection = (
    options: (MachineOption & { machineGroupId: string })[],
    removed: boolean
  ) => {
    const optionIds = options.map((e) => e.value);
    if (removed) {
      setSelectedMachines(
        selectedMachines.filter((e) => !optionIds.includes(e.value))
      );
    } else {
      const newSelectedMachines = [...selectedMachines, ...options];
      setSelectedMachines(newSelectedMachines);
    }
  };

  const onConfirm = () => {
    const savedMachineGroups = selectedMachineGroups.filter(
      (e) => e.machines.length > 0
    );
    appLocalStorage.globalMachinesGroups = savedMachineGroups;
    dispatch(selectMachineGroups(savedMachineGroups, selectedMachines));
    appLocalStorage.globalMachinesGroupMachines = selectedMachines;
    sendEventTrack('machine_group_change', {
      noOfMachineGroups: savedMachineGroups.length,
      noOfMachines: savedMachineGroups.length,
      companyName: currentUser.companies?.[0]?.name
    });
    props.setIsOpen(false);
  };

  const onCancel = () => {
    setSelectedMachines(machineGroups.selectedMachines);
    setSelectedMachineGroupId(machineGroups.selectedMachineGroups[0].id);
    setSelectedMachineGroups(machineGroups.selectedMachineGroups);
    props.setIsOpen(false);
  };

  const machineGroupsList: MachineGroup[] = selectedMachineGroups.find(
    (e) => e.id === 'all-machines'
  )
    ? [{ id: 'all-machines', name: 'All machines', machines: [] }]
    : machineGroups.list.filter((curr) =>
        selectedMachineGroups.map((e) => e.id).includes(curr.id)
      );

  return (
    <div
      className={cn(styles.root, props.classNames)}
      ref={ref}
      data-testid="machine-group-selector"
    >
      <GlobalFilterButton
        selectionText={selectionText}
        machineGroups={machineGroups}
        onClick={() => {
          props.setIsOpen(!props.isOpen);
        }}
        data-testid="machine-group-selector-toggle"
      />
      {props.isOpen && (
        <div
          className={styles['menu-panel']}
          data-testid="machine-group-selector-panel"
        >
          <h4 className={styles.title}>
            <Translate value="machines_selection" />
          </h4>
          <div className={styles['menu-content']}>
            <MachineGroupsContainer
              allowAllMachines={allowAllMachines}
              machineGroups={machineGroups}
              selectedMachineGroups={machineGroupsList}
              onAllMachinesGroupSelection={onAllMachinesGroupSelection}
              onGroupSelection={onGroupSelection}
              selectedMachineGroupId={selectedMachineGroupId}
              data-testid="machine-groups-container"
            />
            <MachinesContainer
              allMachineOptions={allMachineOptions}
              selectedMachines={selectedMachines}
              onAllMachinesSelection={onAllMachinesSelection}
              onMachinesSelection={onMachinesSelection}
              selectedMachineGroupId={selectedMachineGroupId}
              noOfUniqueSelectedMachines={noOfUniqueSelectedMachines}
              data-testid="machines-container"
            />
          </div>
          <ActionButtonRow
            className={styles.actionButtons}
            confirmText={<Translate value="apply" />}
            cancelText={<Translate value="cancel" />}
            onConfirm={onConfirm}
            onCancel={onCancel}
            isDisabled={
              selectedMachineGroups.length === 0 ||
              noOfUniqueSelectedMachines === 0
            }
            data-testid="action-button-row"
          />
        </div>
      )}
    </div>
  );
}
export default MachineGroupSelector;
